/**
 * Minimal toast strip — Stage I uses this to surface layer-permission
 * rejections from the SceneClient so a player who tried to draw on the
 * DM layer (via a stale UI state or a shortcut mishap) sees a clear
 * "you can't write there" instead of a silent no-op.
 *
 * Kept as a module-level store so any code path can fire a toast
 * without plumbing a React context through the whole page. Toasts
 * auto-dismiss after a short timer; the component just reads the
 * current queue via `useSyncExternalStore`.
 */

import { useSyncExternalStore } from "react";
import "./Toasts.css";

const AUTO_DISMISS_MS = 5000;

export type ToastKind = "info" | "warn" | "error";

/**
 * Optional action attached to a toast. When present the toast body becomes
 * clickable — clicking runs `onClick` (and then dismisses the toast). The
 * world-map feature uses this for "A new location has been revealed"
 * notifications that deep-link the user to the marker on the map.
 */
export interface ToastAction {
  onClick: () => void;
  /** Optional short label announced to screen readers, e.g. "View marker". */
  label?: string;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
}

export interface ToastOptions {
  action?: ToastAction;
  /** Override the auto-dismiss timer (ms). Defaults to AUTO_DISMISS_MS. */
  dismissMs?: number;
}

type Listener = () => void;

class ToastStore {
  private toasts: Toast[] = [];
  private listeners = new Set<Listener>();
  private nextId = 1;

  get(): Toast[] {
    return this.toasts;
  }

  push(kind: ToastKind, message: string, options?: ToastOptions): number {
    const id = this.nextId++;
    this.toasts = [
      ...this.toasts,
      { id, kind, message, action: options?.action },
    ];
    this.notify();
    const ms = options?.dismissMs ?? AUTO_DISMISS_MS;
    setTimeout(() => this.dismiss(id), ms);
    return id;
  }

  dismiss(id: number): void {
    const next = this.toasts.filter((t) => t.id !== id);
    if (next.length === this.toasts.length) return;
    this.toasts = next;
    this.notify();
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }
}

export const toastStore = new ToastStore();

export default function Toasts() {
  const toasts = useSyncExternalStore(
    (cb) => toastStore.subscribe(cb),
    () => toastStore.get(),
    () => toastStore.get(),
  );

  if (toasts.length === 0) return null;

  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => {
        const hasAction = Boolean(t.action);
        const handleAction = () => {
          if (!t.action) return;
          t.action.onClick();
          toastStore.dismiss(t.id);
        };
        return (
          <div
            key={t.id}
            className={
              `toasts__item toasts__item--${t.kind}` +
              (hasAction ? ' toasts__item--actionable' : '')
            }
          >
            {hasAction ? (
              <button
                type="button"
                className="toasts__message toasts__message--button"
                onClick={handleAction}
                aria-label={t.action?.label ?? t.message}
              >
                {t.message}
              </button>
            ) : (
              <span className="toasts__message">{t.message}</span>
            )}
            <button
              type="button"
              className="toasts__close"
              onClick={() => toastStore.dismiss(t.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
